import { type ReactNode, useState, lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import Header from "./Header";
import Footer from "./Footer";
import StickyCTA from "./StickyCTA";
import StickyWhatsApp from "./StickyWhatsApp";
const CookieConsent = lazy(() => import("./CookieConsent"));

// Canonical Event Boutique entity node — the ONLY full Organization/LocalBusiness
// on the site (BTQ-33 Phase 1). Every other schema references it via
// { "@id": "https://eventboutique.pt/#org" }. The @id is load-bearing: the BTQ
// worker's Organization declares parentOrganization → this exact @id.
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": ["Organization", "LocalBusiness"],
  "@id": "https://eventboutique.pt/#org",
  name: "Event Boutique",
  legalName: "Btq - Botique de Eventos, Lda",
  slogan: "Destination weddings in Portugal, produced in full.",
  taxID: "517199041",
  url: "https://eventboutique.pt",
  logo: { "@type": "ImageObject", url: "https://eventboutique.pt/logo.png" },
  image: "https://eventboutique.pt/og-image.webp",
  description: "Event Boutique is a destination-wedding production atelier in Portugal, producing a limited number of weddings each year across Lisbon, Cascais, Sintra, Porto, the Douro Valley, the Algarve and Comporta. One team carries each wedding from concept and design through build and on-site direction.",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Rua dos Faisões 131",
    addressLocality: "Cascais",
    addressRegion: "Lisbon",
    postalCode: "2750-005",
    addressCountry: "PT",
  },
  telephone: "+351923272822",
  email: "info@eventboutique.pt",
  priceRange: "€€€€",
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: "09:00",
    closes: "18:00",
  },
  founder: { "@type": "Person", name: "Veronika Tolakh" },
  sameAs: [
    "https://www.instagram.com/event_boutique_portugal",
    "https://pt.pinterest.com/boutiqueventportugal/",
    "https://t.me/event_boutique_pt",
    "https://www.trustpilot.com/review/eventboutique.pt",
    "https://www.casamentos.pt/organizadores-de-casamentos/event-boutique--e157349",
    "https://www.zankyou.pt/f/event-boutique-1023730",
    "https://www.partyslate.com/vendors/event-boutique-pt",
    "https://www.cylex.pt/cascais/event-boutique-187378.html",
  ],
  areaServed: [
    { "@type": "City", name: "Lisbon" },
    { "@type": "City", name: "Cascais" },
    { "@type": "City", name: "Sintra" },
    { "@type": "City", name: "Porto" },
    { "@type": "City", name: "Comporta" },
    { "@type": "AdministrativeArea", name: "Algarve" },
    { "@type": "AdministrativeArea", name: "Douro Valley" },
    { "@type": "AdministrativeArea", name: "Alentejo" },
    { "@type": "AdministrativeArea", name: "Madeira" },
  ],
  knowsAbout: [
    "Destination wedding production in Portugal",
    "Wedding planning in Portugal",
    "Porto wedding venues",
    "Wedding venues in Lisbon",
    "Sintra palace weddings",
    "Douro Valley vineyard weddings",
    "Algarve coastal weddings",
    "Wedding creative direction",
    "Wedding coordination",
    "Private concierge for wedding guests",
  ],
  foundingDate: "2022",
  numberOfEmployees: { "@type": "QuantitativeValue", value: "5-10" },
  subOrganization: {
    "@type": "Organization",
    "@id": "https://btqevents.pt/#organization",
    name: "BTQ Events",
    url: "https://btqevents.pt",
  },
};

const Layout = ({ children }: { children: ReactNode }) => {
  const [cookieBannerVisible, setCookieBannerVisible] = useState(false);

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      </Helmet>
      <Header />
      <main className="flex-1" data-header-theme="light">{children}</main>
      <Footer />
      <StickyCTA offsetBottom={cookieBannerVisible} />
      <StickyWhatsApp offsetBottom={cookieBannerVisible} />
      <Suspense fallback={null}>
        <CookieConsent onVisibilityChange={setCookieBannerVisible} />
      </Suspense>
    </div>
  );
};

export default Layout;
